import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView as useInViewIO } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { 
  ArrowRight, 
  CheckCircle2, 
  Target, 
  Zap, 
  TrendingUp, 
  Users, 
  BarChart3, 
  Sparkles,
  Globe,
  Mail,
  MousePointer2,
  LineChart,
  Settings,
  PieChart,
  Layers,
  RefreshCw
} from 'lucide-react';
import '../styles/premium-homepage.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sleek-website-9/artifacts/9ntuuv4r_white_logo_transparent_background.png';

// Google Logo SVG Component
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Meta Logo SVG Component
const MetaLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#0082FB" d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.92 3.78-3.92 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
  </svg>
);

export default function LandingPagePremium() {
  const [navProgress, setNavProgress] = useState(0);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollYProgress.onChange((v) => {
      setNavProgress(v * 100);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="premium-homepage">
      <PremiumNav navProgress={navProgress} scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      
      {/* Service Tags (replacing marquee) */}
      <ServiceTagsSection />
      
      {/* Redesigned Bento Grid */}
      <BentoGridSection />
      
      {/* Industries We Serve */}
      <IndustriesSection scrollToSection={scrollToSection} />
      
      {/* Scroll Features with Parallax */}
      <ScrollFeaturesSection />
      
      {/* Testimonials */}
      <TestimonialsCarousel />
      
      {/* Case Studies / Our Work */}
      <CaseStudiesSection />
      
      {/* Stats Counter */}
      <StatsCounterSection />
      
      {/* Pricing */}
      <PricingSection scrollToSection={scrollToSection} />
      
      {/* Contact Form */}
      <ContactFormSection />
      
      {/* Footer */}
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
}

// ============ NAVIGATION ============
function PremiumNav({ navProgress, scrollToSection }) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="nav-wrapper">
      <motion.nav 
        className={`pill-nav ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="nav-logo">
          <img src={LOGO_URL} alt="BirdenMedia" />
        </div>
        <div className="nav-links">
          <button onClick={() => scrollToSection('services')}>Services</button>
          <button onClick={() => scrollToSection('pricing')}>Pricing</button>
          <button onClick={() => scrollToSection('work')}>Our Work</button>
        </div>
        <button onClick={() => scrollToSection('contact')} className="nav-cta-primary">
          Get Started
        </button>
      </motion.nav>
      <div className="nav-progress-wrapper">
        <div className="nav-progress-bar" style={{ width: `${navProgress}%` }} />
      </div>
    </header>
  );
}

// ============ HERO SECTION ============
function HeroSection({ scrollToSection }) {
  return (
    <section className="hero-premium">
      <div className="hero-floating-shapes">
        <motion.div 
          className="floating-shape"
          style={{ width: 400, height: 400, top: '10%', left: '5%' }}
          animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="floating-shape"
          style={{ width: 300, height: 300, top: '50%', right: '10%' }}
          animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.h1 
            className="text-5xl lg:text-6xl font-bold leading-tight mb-6 text-gray-900"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            We handle the marketing.<br /><span className="keyword-frame"><span className="keyword-highlight">You run your business.</span></span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-600 mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            BirdenMedia builds complete marketing systems for small and medium businesses — ads, landing pages, follow-ups, and tracking — so you can focus on what you do best.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <button onClick={() => scrollToSection('contact')} className="btn-primary text-lg">
              Book a Free Strategy Call <ArrowRight className="w-5 h-5" />
            </button>
            <button onClick={() => scrollToSection('services')} className="btn-secondary text-lg">
              See What&apos;s Included <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div 
          className="hero-visual-animated"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.div 
            className="animated-card"
            whileHover={{ y: -4 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="text-sm text-gray-600 mb-2">Client Results</div>
            <div className="text-4xl font-bold text-blue-600 mb-1">847 Leads</div>
            <div className="text-sm text-gray-500">Generated This Quarter</div>
          </motion.div>
          
          <motion.div 
            className="animated-card"
            whileHover={{ y: -4 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          >
            <div className="text-sm text-gray-600 mb-2">Average Cost</div>
            <div className="text-4xl font-bold text-blue-600 mb-1">$48.50</div>
            <div className="text-sm text-gray-500">Cost Per Lead</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ SERVICE TAGS (Back to original) ============
function ServiceTagsSection() {
  const services = [
    { icon: GoogleLogo, label: 'Google Ads', isComponent: true },
    { icon: MetaLogo, label: 'Meta Ads', isComponent: true },
    { icon: Layers, label: 'Landing Pages' },
    { icon: Zap, label: 'CRM & Automation' },
    { icon: BarChart3, label: 'Analytics' },
    { icon: RefreshCw, label: 'Optimization' },
  ];

  return (
    <section className="service-tags-section">
      <motion.div 
        className="service-tags-container"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            className="service-tag"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05, y: -2 }}
          >
            {service.isComponent ? (
              <service.icon className="w-5 h-5" />
            ) : (
              <service.icon className="w-5 h-5 text-blue-600" />
            )}
            <span>{service.label}</span>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// ============ REDESIGNED BENTO GRID ============
function BentoGridSection() {
  return (
    <section id="services" className="bento-section-v2">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Everything you need to grow
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            BirdenMedia builds and manages marketing systems for growing businesses. We don&apos;t just run ads — we design the entire flow: how customers find you, how they contact you, how you follow up, and how you track what&apos;s working.
          </p>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mt-4">
            Think of us as your marketing department, without the overhead.
          </p>
        </motion.div>

        <div className="bento-grid-v2">
          {/* Paid Advertising Card */}
          <PaidAdsCard />
          
          {/* Landing Pages Card */}
          <LandingPagesCard />
          
          {/* CRM & Automation Card */}
          <CRMCard />
          
          {/* Analytics Card */}
          <AnalyticsCard />
          
          {/* Optimization Card */}
          <OptimizationCard />
          
          {/* Reporting Card */}
          <ReportingCard />
        </div>
      </div>
    </section>
  );
}

// Animated Stats Card for Paid Ads
function PaidAdsCard() {
  const [currentStats, setCurrentStats] = useState(0);
  const statsData = [
    { impressions: '2.4M', clicks: '48.2K', conversions: '1,247', impChange: '+24%', clickChange: '+18%', convChange: '+32%' },
    { impressions: '3.1M', clicks: '62.8K', conversions: '1,892', impChange: '+31%', clickChange: '+22%', convChange: '+45%' },
    { impressions: '1.8M', clicks: '35.4K', conversions: '956', impChange: '+19%', clickChange: '+15%', convChange: '+28%' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStats((prev) => (prev + 1) % statsData.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const stats = statsData[currentStats];

  return (
    <motion.div 
      className="bento-card-v2 paid-ads-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(59, 130, 246, 0.15)' }}
    >
      {/* Live Campaign Badge - moved to not overlap */}
      <motion.div 
        className="live-campaign-badge"
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="badge-dot"></span>
        Live Campaign
      </motion.div>
      
      <div className="bento-card-header">
        <div className="bento-icon blue">
          <Target className="w-6 h-6" />
        </div>
      </div>
      
      <div className="paid-ads-title-row">
        <h3 className="bento-title">Paid Advertising</h3>
        <div className="bento-platforms">
          <GoogleLogo className="w-5 h-5" />
          <MetaLogo className="w-5 h-5" />
        </div>
      </div>
      
      <p className="bento-desc">We set up and manage your ad campaigns on Google and Facebook. You get more leads — we handle the technical stuff.</p>
      
      <div className="bento-stats-grid">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStats}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="stats-row"
          >
            <div className="stat-item">
              <span className="stat-label">Impressions</span>
              <span className="stat-value">{stats.impressions}</span>
              <span className="stat-change positive">{stats.impChange}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Clicks</span>
              <span className="stat-value">{stats.clicks}</span>
              <span className="stat-change positive">{stats.clickChange}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Conversions</span>
              <span className="stat-value">{stats.conversions}</span>
              <span className="stat-change positive">{stats.convChange}</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Landing Pages Card with alternating mockups
function LandingPagesCard() {
  const [currentPage, setCurrentPage] = useState(0);
  const pages = [
    { industry: 'Construction', color: '#f59e0b', accent: '#fbbf24' },
    { industry: 'Real Estate', color: '#3b82f6', accent: '#60a5fa' },
    { industry: 'Healthcare', color: '#10b981', accent: '#34d399' },
    { industry: 'Home Services', color: '#8b5cf6', accent: '#a78bfa' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % pages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const page = pages[currentPage];

  return (
    <motion.div 
      className="bento-card-v2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(139, 92, 246, 0.15)' }}
    >
      <div className="bento-card-header">
        <div className="bento-icon purple">
          <Layers className="w-6 h-6" />
        </div>
      </div>
      
      <h3 className="bento-title">Landing Pages</h3>
      <p className="bento-desc">Custom pages built to turn visitors into leads. No generic templates — designed for your industry.</p>
      
      <div className="landing-page-mockup">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            className="mockup-browser"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          >
            <div className="browser-header">
              <div className="browser-dots">
                <span style={{ background: '#ef4444' }}></span>
                <span style={{ background: '#f59e0b' }}></span>
                <span style={{ background: '#10b981' }}></span>
              </div>
              <div className="browser-url">{page.industry.toLowerCase().replace(' ', '')}.com</div>
            </div>
            <div className="browser-content">
              <div className="page-hero" style={{ background: `linear-gradient(135deg, ${page.color}, ${page.accent})` }}>
                <div className="page-nav"></div>
                <div className="page-headline"></div>
                <div className="page-subline"></div>
                <div className="page-cta" style={{ background: 'white' }}></div>
              </div>
              <div className="page-features">
                <div className="feature-block"></div>
                <div className="feature-block"></div>
                <div className="feature-block"></div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="industry-label">{page.industry}</div>
      </div>
    </motion.div>
  );
}

// CRM Card
function CRMCard() {
  return (
    <motion.div 
      className="bento-card-v2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(16, 185, 129, 0.15)' }}
    >
      <div className="bento-card-header">
        <div className="bento-icon green">
          <Zap className="w-6 h-6" />
        </div>
      </div>
      
      <h3 className="bento-title">CRM & Automation</h3>
      <p className="bento-desc">When a lead comes in, they get an instant response. No more missed opportunities or forgotten follow-ups.</p>
      
      <div className="automation-flow">
        <motion.div 
          className="flow-step"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, delay: 0 }}
        >
          <div className="flow-icon lead">📥</div>
          <span>Lead In</span>
        </motion.div>
        <div className="flow-arrow">→</div>
        <motion.div 
          className="flow-step"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
        >
          <div className="flow-icon email">📧</div>
          <span>Email</span>
        </motion.div>
        <div className="flow-arrow">→</div>
        <motion.div 
          className="flow-step"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        >
          <div className="flow-icon sms">💬</div>
          <span>SMS</span>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Analytics Card
function AnalyticsCard() {
  return (
    <motion.div 
      className="bento-card-v2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(245, 158, 11, 0.15)' }}
    >
      <div className="bento-card-header">
        <div className="bento-icon orange">
          <BarChart3 className="w-6 h-6" />
        </div>
      </div>
      
      <h3 className="bento-title">Analytics</h3>
      <p className="bento-desc">See exactly where your leads come from and what&apos;s working — in plain English, not marketing jargon.</p>
      
      <div className="mini-chart-container">
        <motion.div className="chart-bars-animated">
          {[40, 65, 45, 80, 55, 90, 70].map((height, i) => (
            <motion.div
              key={i}
              className="chart-bar-animated"
              initial={{ height: 0 }}
              whileInView={{ height: `${height}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
}

// Optimization Card
function OptimizationCard() {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      className="bento-card-v2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.4 }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(20, 184, 166, 0.15)' }}
    >
      <div className="bento-card-header">
        <div className="bento-icon teal">
          <RefreshCw className="w-6 h-6" />
        </div>
      </div>
      
      <h3 className="bento-title">Ongoing Optimization</h3>
      <p className="bento-desc">We don&apos;t &quot;set it and forget it.&quot; Every week, we look at the data and make adjustments to improve your results.</p>
      
      <div className="optimization-visual">
        <div className="opt-circle">
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8"/>
            <motion.circle 
              cx="50" cy="50" r="45" fill="none" stroke="#14b8a6" strokeWidth="8"
              strokeDasharray={283}
              strokeDashoffset={283 - (283 * progress / 100)}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="opt-percent">{Math.round(progress)}%</div>
        </div>
        <span className="opt-label">Performance Score</span>
      </div>
    </motion.div>
  );
}

// Reporting Card
function ReportingCard() {
  return (
    <motion.div 
      className="bento-card-v2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.5 }}
      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(239, 68, 68, 0.15)' }}
    >
      <div className="bento-card-header">
        <div className="bento-icon red">
          <PieChart className="w-6 h-6" />
        </div>
      </div>
      
      <h3 className="bento-title">Monthly Reporting</h3>
      <p className="bento-desc">Every month you get a clear report showing what we did, what worked, and what&apos;s next. No surprises.</p>
      
      <div className="report-preview">
        <motion.div 
          className="report-card"
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <div className="report-header">
            <span className="report-month">December Report</span>
            <span className="report-badge">PDF</span>
          </div>
          <div className="report-stats">
            <div className="report-stat">
              <span className="rs-value">$48.50</span>
              <span className="rs-label">Avg CPL</span>
            </div>
            <div className="report-stat">
              <span className="rs-value">4.2x</span>
              <span className="rs-label">ROI</span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ============ INDUSTRIES WE SERVE ============
function IndustriesSection({ scrollToSection }) {
  const industries = [
    {
      name: 'Construction & Contractors',
      description: 'Roofing, remodeling, general contractors, and trades',
      icon: '🏗️',
      highlight: true
    },
    {
      name: 'Home Services',
      description: 'Cleaning, landscaping, HVAC, plumbing, and more',
      icon: '🏠',
      highlight: false
    },
    {
      name: 'Real Estate',
      description: 'Agents, investors, and property management',
      icon: '🏢',
      highlight: false
    },
    {
      name: 'Healthcare',
      description: 'Dental, chiropractic, med spas, and clinics',
      icon: '⚕️',
      highlight: false
    },
    {
      name: 'Professional Services',
      description: 'Law firms, accountants, and consultants',
      icon: '💼',
      highlight: false
    },
    {
      name: 'Local Business',
      description: 'Retail, restaurants, and service providers',
      icon: '📍',
      highlight: false
    }
  ];

  return (
    <section className="industries-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Industries we work with
          </h2>
          <p className="text-xl text-gray-600">
            We&apos;ve helped businesses like yours get more leads and grow. Here&apos;s who we work best with.
          </p>
        </motion.div>

        <div className="industries-grid">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              className={`industry-card ${industry.highlight ? 'industry-card-featured' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -6 }}
            >
              {industry.highlight && <div className="industry-featured-badge">Most Popular</div>}
              <span className="industry-icon">{industry.icon}</span>
              <h3 className="industry-name">{industry.name}</h3>
              <p className="industry-desc">{industry.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-600 mb-4">Don&apos;t see your industry? We probably still can help.</p>
          <button onClick={() => scrollToSection('contact')} className="btn-outline">
            Let&apos;s Chat <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ============ SCROLL FEATURES WITH PARALLAX ============
function ScrollFeaturesSection() {
  const features = [
    {
      title: "Capture More Leads",
      description: "Strategic ad campaigns and high-converting landing pages designed to bring in qualified prospects consistently.",
      mockup: 'leads'
    },
    {
      title: "Follow Up Automatically",
      description: "CRM automation ensures every lead gets the right message at the right time — without manual effort.",
      mockup: 'automation'
    },
    {
      title: "Track Everything",
      description: "Real-time dashboards show exactly what's working so you can make informed decisions.",
      mockup: 'tracking'
    },
    {
      title: "Scale With Confidence",
      description: "Systems built to grow with your business. What works at $1K/month works at $25K/month.",
      mockup: 'scale'
    }
  ];

  return (
    <section className="scroll-features-section">
      <div className="max-w-7xl mx-auto px-6">
        {features.map((feature, index) => (
          <FeatureRow key={index} feature={feature} index={index} isEven={index % 2 === 0} />
        ))}
      </div>
    </section>
  );
}

function FeatureRow({ feature, index, isEven }) {
  const [ref, inView] = useInViewIO({ threshold: 0.3, triggerOnce: false });
  
  return (
    <motion.div 
      ref={ref}
      className={`feature-row ${isEven ? '' : 'reversed'}`}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
      transition={{ duration: 0.6 }}
    >
      <div className="feature-text">
        <motion.div 
          className="feature-number"
          animate={inView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0.5 }}
        >
          0{index + 1}
        </motion.div>
        <h3>{feature.title}</h3>
        <p>{feature.description}</p>
      </div>
      
      <motion.div 
        className="feature-visual"
        animate={inView ? { x: 0, opacity: 1 } : { x: isEven ? 50 : -50, opacity: 0.5 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <FeatureMockup type={feature.mockup} />
      </motion.div>
    </motion.div>
  );
}

function FeatureMockup({ type }) {
  if (type === 'leads') {
    return (
      <div className="mockup-container leads-mockup">
        <div className="mockup-window">
          <div className="window-header">
            <div className="window-dots"><span></span><span></span><span></span></div>
            <span>Lead Dashboard</span>
          </div>
          <div className="window-content">
            <motion.div className="lead-item" animate={{ x: [0, 5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              <span className="lead-dot hot"></span>
              <span>New Lead - John D.</span>
              <span className="lead-time">Just now</span>
            </motion.div>
            <motion.div className="lead-item" animate={{ x: [0, 5, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}>
              <span className="lead-dot warm"></span>
              <span>Follow Up - Sarah M.</span>
              <span className="lead-time">2m ago</span>
            </motion.div>
            <motion.div className="lead-item" animate={{ x: [0, 5, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}>
              <span className="lead-dot hot"></span>
              <span>New Lead - Mike R.</span>
              <span className="lead-time">5m ago</span>
            </motion.div>
          </div>
        </div>
        <motion.div 
          className="floating-notification"
          animate={{ y: [0, -10, 0], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          🎉 +3 leads today!
        </motion.div>
      </div>
    );
  }
  
  if (type === 'automation') {
    return (
      <div className="mockup-container automation-mockup">
        <div className="automation-pipeline">
          <motion.div 
            className="pipeline-node active"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="node-icon">📥</span>
            <span>Lead Captured</span>
          </motion.div>
          <motion.div 
            className="pipeline-connector"
            animate={{ scaleX: [0, 1] }}
            transition={{ duration: 0.5, delay: 0.5 }}
          />
          <motion.div 
            className="pipeline-node"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
          >
            <span className="node-icon">📧</span>
            <span>Email Sent</span>
          </motion.div>
          <motion.div 
            className="pipeline-connector"
            animate={{ scaleX: [0, 1] }}
            transition={{ duration: 0.5, delay: 1 }}
          />
          <motion.div 
            className="pipeline-node"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 1 }}
          >
            <span className="node-icon">💬</span>
            <span>SMS Follow-up</span>
          </motion.div>
        </div>
      </div>
    );
  }
  
  if (type === 'tracking') {
    return (
      <div className="mockup-container tracking-mockup">
        <div className="tracking-dashboard">
          <div className="tracking-chart">
            <svg viewBox="0 0 200 80" className="line-chart">
              <motion.path
                d="M0,60 Q30,55 50,40 T100,30 T150,20 T200,10"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="3"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </svg>
          </div>
          <div className="tracking-metrics">
            <div className="metric">
              <span className="metric-value">$48.50</span>
              <span className="metric-label">CPL</span>
            </div>
            <div className="metric">
              <span className="metric-value">2.8%</span>
              <span className="metric-label">CTR</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (type === 'scale') {
    return (
      <div className="mockup-container scale-mockup">
        <motion.div 
          className="scale-visual"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <div className="scale-bar">
            <motion.div 
              className="scale-fill"
              animate={{ width: ['30%', '90%', '30%'] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
          </div>
          <div className="scale-labels">
            <span>$1K/mo</span>
            <span>$25K/mo</span>
          </div>
          <div className="scale-result">
            <span className="result-label">Same CPL:</span>
            <span className="result-value">$48.50</span>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return null;
}

// ============ TESTIMONIALS CAROUSEL ============
function TestimonialsCarousel() {
  const testimonials = [
    { quote: "BirdenMedia took marketing off my plate completely. I used to spend hours trying to figure out ads — now I just focus on running my business.", author: "Michael R.", role: "Real Estate Investor", metric: "2.5x more leads" },
    { quote: "The reporting alone is worth it. I finally understand where my money is going and what is actually working.", author: "Sarah J.", role: "E-commerce Owner", metric: "32% lower CPL" },
    { quote: "They built us a system that actually scales. Our ad spend tripled but our cost per lead stayed flat.", author: "David K.", role: "Home Services", metric: "Stable $52 CPL" },
    { quote: "No more guessing games. Real data, real results, and a team that actually explains things in plain English.", author: "Jennifer L.", role: "Healthcare Practice", metric: "3.8x ROI" }
  ];

  return (
    <section className="testimonials-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What our clients say</h2>
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className={`w-6 h-6 ${star <= 4 ? 'text-yellow-400' : 'text-yellow-400'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-2xl font-bold text-gray-900">4.9</span>
            <span className="text-gray-500">from client feedback</span>
          </div>
          <p className="text-xl text-gray-600">Real feedback from business owners like you.</p>
        </motion.div>

        <div className="testimonials-track-container">
          <motion.div className="testimonials-track" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}>
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-metric">{testimonial.metric}</div>
                <p className="testimonial-quote">&quot;{testimonial.quote}&quot;</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.author.charAt(0)}</div>
                  <div>
                    <div className="author-name">{testimonial.author}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ CASE STUDIES SECTION ============
function CaseStudiesSection() {
  const [currentCase, setCurrentCase] = useState(0);
  
  const caseStudies = [
    {
      industry: 'Construction',
      client: 'Martinez Remodeling',
      challenge: 'New construction company struggling to compete against established contractors. Needed a steady stream of residential project leads.',
      solution: 'Built a targeted Google Ads campaign focused on home additions and renovations. Created dedicated landing pages showcasing past projects with before/after galleries.',
      results: [
        { metric: '34', label: 'Leads/Month' },
        { metric: '$58', label: 'Cost Per Lead' },
        { metric: '3.4x', label: 'ROI' }
      ],
      timeframe: 'Results after 90 days',
      color: '#3b82f6'
    },
    {
      industry: 'Home Services',
      client: 'PrimeTemp HVAC',
      challenge: 'Seasonal business with inconsistent lead flow. Previous marketing efforts were expensive with poor tracking.',
      solution: 'Launched Google Ads targeting emergency repairs and seasonal tune-ups. Set up call tracking and automated text follow-ups for missed calls.',
      results: [
        { metric: '41', label: 'Leads/Month' },
        { metric: '$47', label: 'Cost Per Lead' },
        { metric: '4.1x', label: 'ROI' }
      ],
      timeframe: 'Results after 4 months',
      color: '#10b981'
    },
    {
      industry: 'Construction',
      client: 'SolidHome Concrete',
      challenge: 'Concrete contractor needed more residential driveway and patio jobs. Word-of-mouth alone was not filling the schedule.',
      solution: 'Created Google and Facebook campaigns targeting homeowners searching for concrete services. Built a lead qualification funnel with project estimate requests.',
      results: [
        { metric: '29', label: 'Leads/Month' },
        { metric: '$54', label: 'Cost Per Lead' },
        { metric: '3.8x', label: 'ROI' }
      ],
      timeframe: 'Results after 90 days',
      color: '#8b5cf6'
    }
  ];

  const nextCase = () => setCurrentCase((prev) => (prev + 1) % caseStudies.length);
  const prevCase = () => setCurrentCase((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);

  const study = caseStudies[currentCase];

  return (
    <section id="work" className="case-studies-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Real results, real businesses</h2>
          <p className="text-xl text-gray-600">Here is what happens when marketing actually works.</p>
        </motion.div>

        <div className="case-study-carousel">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentCase}
              className="case-study-card"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
            >
              <div className="case-study-header">
                <span className="case-industry" style={{ backgroundColor: `${study.color}15`, color: study.color }}>
                  {study.industry}
                </span>
                <span className="case-client">{study.client}</span>
              </div>

              <div className="case-study-content">
                <div className="case-section">
                  <h4>The Challenge</h4>
                  <p>{study.challenge}</p>
                </div>

                <div className="case-section">
                  <h4>What We Did</h4>
                  <p>{study.solution}</p>
                </div>

                <div className="case-results">
                  <h4>The Results</h4>
                  <div className="results-grid">
                    {study.results.map((result, idx) => (
                      <motion.div 
                        key={idx} 
                        className="result-item"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <span className="result-metric" style={{ color: study.color }}>{result.metric}</span>
                        <span className="result-label">{result.label}</span>
                      </motion.div>
                    ))}
                  </div>
                  <span className="case-timeframe">{study.timeframe}</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="case-nav">
            <button onClick={prevCase} className="case-nav-btn">
              ←
            </button>
            <div className="case-dots">
              {caseStudies.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentCase(idx)}
                  className={`case-dot ${idx === currentCase ? 'active' : ''}`}
                  style={{ backgroundColor: idx === currentCase ? study.color : undefined }}
                />
              ))}
            </div>
            <button onClick={nextCase} className="case-nav-btn">
              →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ STATS COUNTER ============
function StatsCounterSection() {
  const [ref, inView] = useInViewIO({ triggerOnce: true, threshold: 0.3 });

  return (
    <section className="py-20 px-6" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="counter-section">
          <div className="counter-grid">
            <div className="counter-item">
              <div className="counter-number">{inView && <CountUp end={485} duration={2.5} prefix="$" suffix="K+" />}</div>
              <div className="counter-label">Managed in Ad Spend</div>
            </div>
            <div className="counter-item">
              <div className="counter-number">{inView && <CountUp end={38} duration={2.5} suffix="+" />}</div>
              <div className="counter-label">Clients Served</div>
            </div>
            <div className="counter-item">
              <div className="counter-number">{inView && <CountUp end={3.6} decimals={1} duration={2.5} suffix="x" />}</div>
              <div className="counter-label">Avg Client ROI</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ PRICING SECTION ============
function PricingSection({ scrollToSection }) {
  const pricingTiers = [
    { 
      name: 'Starter', 
      price: '$950', 
      period: '/ month', 
      commitment: '3-month minimum', 
      description: 'Perfect if you want help with ads but prefer to handle follow-up yourself.',
      includes: [
        'Ad setup on one platform (Google or Facebook)',
        'Campaign strategy and structure',
        'Ad copy creation',
        'Basic performance optimization',
        'Monthly performance summary',
        'Client dashboard access'
      ],
      youHandle: ['Lead follow-up', 'Scheduling', 'Closing'],
      cta: 'Get Started',
      popular: false
    },
    { 
      name: 'Growth', 
      price: '$1,500', 
      period: '/ month', 
      commitment: '3-month minimum', 
      description: 'Our most popular plan. Ads on multiple platforms plus landing pages and tracking.',
      includes: [
        'Google and Facebook ads',
        'Landing page setup',
        'Conversion tracking',
        'Ongoing optimization',
        'Lead delivery via email or CRM',
        'Monthly strategy review',
        'Client dashboard access'
      ],
      youHandle: ['Lead follow-up and closing'],
      cta: 'Book a Call',
      popular: true
    },
    { 
      name: 'Partner', 
      price: '$2,500', 
      period: '/ month', 
      commitment: '3-month minimum', 
      description: 'The full package. We handle everything from ads to automated follow-ups.',
      includes: [
        'Multi-platform ads (Google + Facebook)',
        'High-converting landing pages',
        'CRM setup & automation',
        'Instant lead notifications',
        'Appointment booking integration',
        'Monthly strategy calls',
        'Priority support',
        'Client dashboard access'
      ],
      youHandle: ['Showing up to close booked leads'],
      cta: 'Apply Now',
      popular: false
    }
  ];

  return (
    <section id="pricing" className="pricing-section-v2">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Simple pricing. No surprises.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Pick a plan that fits your business. Every plan includes strategy, setup, and ongoing management — you don&apos;t have to figure anything out yourself.
          </p>
        </motion.div>

        {/* Dashboard Feature Highlight */}
        <motion.div 
          className="dashboard-highlight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="dashboard-highlight-icon">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div className="dashboard-highlight-text">
            <span className="dashboard-highlight-title">Client Dashboard Included</span>
            <span className="dashboard-highlight-desc">Track your leads, campaigns, and ROI in real-time — all in one place.</span>
          </div>
        </motion.div>

        <div className="pricing-grid-v2">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={index}
              className={`pricing-card-v2 ${tier.popular ? 'pricing-card-v2-popular' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              {tier.popular && <div className="pricing-badge-v2">Most Popular</div>}
              
              <div className="pricing-card-v2-header">
                <h3>{tier.name}</h3>
                <div className="pricing-amount">
                  <span className="price">{tier.price}</span>
                  <span className="period">{tier.period}</span>
                </div>
                <span className="commitment">{tier.commitment}</span>
              </div>
              
              <p className="pricing-desc">{tier.description}</p>
              
              <div className="pricing-includes">
                <p className="includes-label">What&apos;s included:</p>
                <ul>
                  {tier.includes.map((item, i) => (
                    <li key={i}>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="pricing-handle">
                <p className="handle-label">You handle:</p>
                <ul>
                  {tier.youHandle.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <button 
                onClick={() => scrollToSection('contact')}
                className={`pricing-cta ${tier.popular ? 'primary' : 'secondary'}`}
              >
                {tier.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* 3-Month Explanation */}
        <motion.div 
          className="commitment-explanation"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h4>Why do we ask for 3 months?</h4>
          <p>Because real marketing takes time. Month one is setup and testing. Month two is optimization. By month three, you are seeing consistent results. We do not do quick fixes — we build systems that keep working.</p>
        </motion.div>

        {/* Spanish Support */}
        <motion.div 
          className="spanish-support"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Globe className="w-4 h-4" />
          <span><strong>Hablamos español.</strong> Nuestro equipo puede trabajar con clientes en inglés y español.</span>
        </motion.div>
      </div>
    </section>
  );
}

// ============ CONTACT FORM ============
function ContactFormSection() {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  const [formData, setFormData] = useState({ 
    firstName: '', 
    lastName: '',
    company: '', 
    serviceType: '', 
    email: '', 
    phone: '', 
    budget: '',
    timeline: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };
  
  const handleSubmit = async (e) => { 
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Map service type to backend enum values
      const serviceTypeMap = {
        'Construction/Contractors': 'construction',
        'Home Services': 'home_services',
        'Healthcare': 'medspa',
        'Professional Services': 'legal',
        'Real Estate': 'other',
        'Local Business': 'other',
        'Other': 'other'
      };
      
      const response = await fetch(`${API}/api/lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          company: formData.company,
          email: formData.email,
          phone: formData.phone || '0000000000', // Default if not provided
          service_type: serviceTypeMap[formData.serviceType] || 'other',
          budget: formData.budget || '1-3k',
          timeline: formData.timeline || 'exploring',
          message: formData.message,
          consent: formData.consent,
          utm_source: sessionStorage.getItem('utm_source') || null,
          utm_medium: sessionStorage.getItem('utm_medium') || null,
          utm_campaign: sessionStorage.getItem('utm_campaign') || null
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Unable to submit form. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section-v2">
      <div className="max-w-6xl mx-auto px-6">
        <div className="contact-wrapper">
          {/* Left Side - Info */}
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to stop guessing and start growing?</h2>
            <p>Tell us a bit about your business. We will reach out within 24 hours to schedule a quick call — no pitch, just an honest conversation about whether we are a good fit.</p>
            
            <div className="contact-features">
              <div className="contact-feature">
                <CheckCircle2 className="w-5 h-5" />
                <span>15-minute intro call</span>
              </div>
              <div className="contact-feature">
                <CheckCircle2 className="w-5 h-5" />
                <span>We will share what is working in your industry</span>
              </div>
              <div className="contact-feature">
                <CheckCircle2 className="w-5 h-5" />
                <span>No obligation — just straight talk</span>
              </div>
            </div>

            <div className="contact-direct">
              <p>Prefer to reach out directly?</p>
              <a href="mailto:hello@birdenmedia.co" className="contact-email">
                <Mail className="w-5 h-5" />
                hello@birdenmedia.co
              </a>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div 
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="form-success">
                <CheckCircle2 className="w-16 h-16" />
                <h3>Message Received!</h3>
                <p>We will be in touch within 24 hours to schedule your call.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form-v2">
                {error && (
                  <div className="form-error" style={{ background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                    {error}
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-field">
                    <label>First Name</label>
                    <input 
                      type="text" 
                      name="firstName" 
                      value={formData.firstName} 
                      onChange={handleChange} 
                      required 
                      placeholder="First name" 
                    />
                  </div>
                  <div className="form-field">
                    <label>Last Name</label>
                    <input 
                      type="text" 
                      name="lastName" 
                      value={formData.lastName} 
                      onChange={handleChange} 
                      required 
                      placeholder="Last name" 
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-field">
                    <label>Business Name</label>
                    <input 
                      type="text" 
                      name="company" 
                      value={formData.company} 
                      onChange={handleChange} 
                      required 
                      placeholder="Your company" 
                    />
                  </div>
                  <div className="form-field">
                    <label>Industry</label>
                    <select 
                      name="serviceType" 
                      value={formData.serviceType} 
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Select your industry</option>
                      <option value="Construction/Contractors">Construction / Contractors</option>
                      <option value="Home Services">Home Services</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Professional Services">Professional Services</option>
                      <option value="Local Business">Local Business</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-field">
                    <label>Email</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      placeholder="you@company.com" 
                    />
                  </div>
                  <div className="form-field">
                    <label>Phone <span className="optional">(optional)</span></label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      placeholder="(555) 123-4567" 
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-field">
                    <label>Monthly Budget</label>
                    <select 
                      name="budget" 
                      value={formData.budget} 
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Select budget range</option>
                      <option value="<1k">Under $1,000/mo</option>
                      <option value="1-3k">$1,000 - $3,000/mo</option>
                      <option value="3-6k">$3,000 - $6,000/mo</option>
                      <option value="6-10k">$6,000 - $10,000/mo</option>
                      <option value="10k+">$10,000+/mo</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Timeline</label>
                    <select 
                      name="timeline" 
                      value={formData.timeline} 
                      onChange={handleChange} 
                      required
                    >
                      <option value="">When do you want to start?</option>
                      <option value="asap">ASAP</option>
                      <option value="1-3mo">In 1-3 months</option>
                      <option value="exploring">Just exploring</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-field">
                  <label>How can we help? <span className="optional">(optional)</span></label>
                  <textarea 
                    name="message" 
                    value={formData.message} 
                    onChange={handleChange} 
                    rows={3} 
                    placeholder="Tell us about your business and marketing goals..."
                  />
                </div>
                
                <div className="form-field" style={{ marginBottom: '16px' }}>
                  <label className="consent-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      name="consent" 
                      checked={formData.consent} 
                      onChange={handleChange}
                      required
                      style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>
                      I agree to be contacted by BirdenMedia about marketing services. 
                      View our <a href="/privacy" target="_blank" style={{ color: '#3b82f6' }}>Privacy Policy</a>.
                    </span>
                  </label>
                </div>
                
                <button type="submit" className="form-submit" disabled={isSubmitting || !formData.consent}>
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <span>Get Started</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ FOOTER ============
function Footer({ scrollToSection }) {
  return (
    <footer className="footer-v2">
      <div className="max-w-6xl mx-auto px-6">
        <div className="footer-main">
          <div className="footer-brand">
            <img src={LOGO_URL} alt="BirdenMedia" />
            <p>We handle the marketing. You run your business.</p>
          </div>
          
          <div className="footer-links">
            <div className="footer-col">
              <h4>Company</h4>
              <button onClick={() => scrollToSection('services')}>Services</button>
              <button onClick={() => scrollToSection('pricing')}>Pricing</button>
              <button onClick={() => scrollToSection('work')}>Our Work</button>
              <button onClick={() => scrollToSection('contact')}>Contact</button>
            </div>
            
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="/cookies">Cookie Policy</a>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} BirdenMedia. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
